import { useState, useCallback } from 'react';

interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  retryCondition?: (error: any) => boolean;
}

export function useRetry<T>(
  asyncFn: () => Promise<T>,
  options: RetryOptions = {}
) {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
    retryCondition = () => true
  } = options;

  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const execute = useCallback(async (): Promise<T> => {
    setError(null);
    let lastError: Error | null = null;
    let delay = initialDelay;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          setIsRetrying(true);
          setRetryCount(attempt);
          
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Exponential backoff
          delay = Math.min(delay * backoffMultiplier, maxDelay);
        }

        const result = await asyncFn();
        
        // Success - reset state
        setIsRetrying(false);
        setRetryCount(0);
        
        return result;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        
        // Check if we should retry this error
        if (!retryCondition(lastError)) {
          throw lastError;
        }

        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          setIsRetrying(false);
          setError(lastError);
          throw lastError;
        }
      }
    }

    // Should never reach here, but TypeScript needs it
    throw lastError || new Error('Retry failed');
  }, [asyncFn, maxRetries, initialDelay, maxDelay, backoffMultiplier, retryCondition]);

  const reset = useCallback(() => {
    setIsRetrying(false);
    setRetryCount(0);
    setError(null);
  }, []);

  return {
    execute,
    isRetrying,
    retryCount,
    error,
    reset
  };
}

// Helper function to check if error is retryable
export function isRetryableError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = error.message?.toLowerCase() || String(error).toLowerCase();
  
  // Network errors - retryable
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('timeout') ||
    errorMessage.includes('econnreset') ||
    errorMessage.includes('enotfound')
  ) {
    return true;
  }
  
  // Rate limit - retryable
  if (errorMessage.includes('rate limit') || errorMessage.includes('429')) {
    return true;
  }
  
  // Server errors (5xx) - retryable
  if (errorMessage.includes('500') || errorMessage.includes('502') || errorMessage.includes('503') || errorMessage.includes('504')) {
    return true;
  }
  
  // Auth errors - not retryable
  if (errorMessage.includes('unauthorized') || errorMessage.includes('401') || errorMessage.includes('403')) {
    return false;
  }
  
  // Not found - not retryable
  if (errorMessage.includes('not found') || errorMessage.includes('404')) {
    return false;
  }
  
  // Default: retryable for unknown errors
  return true;
}

export default useRetry;

